import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { RuleEngineService } from "../../services/rule-engine.service";
import { Observable, Subscription } from "rxjs";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { combineLatest, startWith, map } from "rxjs";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: "app-predicate-filter",
  templateUrl: "./predicate-filter.component.html",
  styleUrls: ["./predicate-filter.component.scss"],
})
export class PredicateFilterComponent implements OnInit, OnDestroy {
  constructor(private toastr: ToastrService,
    ruleEngineService: RuleEngineService, fb: FormBuilder,public dialogRef: MatDialogRef<PredicateFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
	) {
    this._ruleEngineService = ruleEngineService;
    this._fb = fb;
  }
  private _ruleEngineService!: RuleEngineService;
  private _fb: FormBuilder;
  private _subConditionsSubscription!: Subscription;
  subConditionsAndTheirAttributes: any;
  activeTab: number = 0;
  subConditionNumber: number = 1;
  isSubmitDisabled: boolean = true;
  invalidFormMessage: string = "";
  filteredOptionsMap: Map<string, Observable<string[]>> = new Map();
  isFormValid: boolean = false;

  ngOnInit(): void {
    this._subConditionsSubscription =
      this._ruleEngineService.$getSubConditionsFor.subscribe({
        next: (data) => {
          this.subConditionsAndTheirAttributes = data;
          this.setupFiltering();
        },
        error: (err) => {this.toastr.error("Unable to get sub conditions", "", {
          timeOut: 4000,
          positionClass: 'toast-bottom-right'
        });}
      });
  }

  //#region This code is used for filtering the data when user types in the input box
  private setupFiltering(): void {
    if (!this.subConditionsAndTheirAttributes) return;
    
    this.subConditionsAndTheirAttributes.forEach((tab: any, tabIndex: number) => {
      const groupArray = tab.formGroup.get('groupArray') as FormArray;
      groupArray.controls.forEach((group: any, groupIndex: number) => {
        const conditions = group.get('conditions') as FormArray;
        conditions.controls.forEach((condition: any, conditionIndex: number) => {
          this.setupFilteringForControl(tabIndex, groupIndex, conditionIndex);
        });
      });
    });
  }

  private setupFilteringForControl(tabIndex: number, groupIndex: number, conditionIndex: number): void {
    const tab = this.subConditionsAndTheirAttributes[tabIndex];
    if (!tab) return;

    const groupArray = tab.formGroup.get('groupArray') as FormArray;
    const group = groupArray.at(groupIndex);
    if (!group) return;

    const conditions = group.get('conditions') as FormArray;
    const condition = conditions.at(conditionIndex);
    if (!condition) return;

    const attributeControl = condition.get('attribute');
    if (attributeControl) {
      const controlId = `${tabIndex}-${groupIndex}-${conditionIndex}`;
      this.filteredOptionsMap.set(
        controlId,
        attributeControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filter(value as string, tab.attributes || []))
        )
      );
    }
  }

  private _filter(value: string, options: string[]): string[] {
    const filterValue = value.toLowerCase();
    return options.filter(option => 
      option.toLowerCase().includes(filterValue)
    );
  }

  getFilteredOptions(tabIndex: number, groupIndex: number, conditionIndex: number): Observable<string[]> {
    const controlId = `${tabIndex}-${groupIndex}-${conditionIndex}`;
    return this.filteredOptionsMap.get(controlId) || new Observable(observer => {
      observer.next([]);
      observer.complete();
    });
  }
  //#endregion

  getConditionsForTab(groupIndex: number): FormArray {
    return this.subConditionsAndTheirAttributes[this.activeTab]?.formGroup
      .get("groupArray")
      .at(groupIndex)
      .get("conditions") as FormArray;
  }

  getGroupFormArray(): FormArray {
    return this.subConditionsAndTheirAttributes[this.activeTab]?.formGroup?.get(
      "groupArray"
    ) as FormArray;
  }
  removeGroup(activeTab: number, activeGroup: number) {
    const groups = this.subConditionsAndTheirAttributes[
      activeTab
    ]?.formGroup.get("groupArray") as FormArray;
    groups.removeAt(activeGroup);
  }

  // Create an empty condition group
  createConditionGroup(): FormGroup {
    return this._fb.group({
      attribute: this._fb.control("", Validators.required),
      condition: this._fb.control("", Validators.required),
      value: this._fb.control("", Validators.required),
    });
  }

  // Add a new condition to the active tab's FormArray
  addCondition(activeTab: number, activeGroup: number): void {
    const conditions = this.subConditionsAndTheirAttributes[
      activeTab
    ]?.formGroup
      ?.get("groupArray")
      .controls[activeGroup].get("conditions") as FormArray;
    conditions.push(this.createConditionGroup());
    
    // Set up filtering for the new condition
    const newConditionIndex = conditions.length - 1;
    this.setupFilteringForControl(activeTab, activeGroup, newConditionIndex);
  }
  // Remove a condition from the active tab's FormArray
  removeCondition(
    activeTab: number,
    activeGroup: number,
    conditionIndex: number
  ): void {
    const conditions = this.subConditionsAndTheirAttributes[
      activeTab
    ]?.formGroup
      ?.get("groupArray")
      .controls[activeGroup].get("conditions") as FormArray;
    conditions.removeAt(conditionIndex);
  }

  ngOnDestroy(): void {
    if (this._subConditionsSubscription) {
      this._subConditionsSubscription.unsubscribe();
    }
  }

  onClickClose() {
    this._ruleEngineService.setPopUpValue(false);
	this.dialogRef.close();
  }

  onClickTab(index: number, subConditonFor: any) {
    this.activeTab = index;
  }

  addGroup() {
    return this._fb.group({
      logicalOperator: this._fb.control("AND", Validators.required), // Default to 'AND'
      conditions: this._fb.array([this.createConditionGroup()]), // Start with one empty condition
    });
  }
  addGroupForActiveIndex(activeTab: number) {
    const emptyGroup = this._fb.group({
      logicalOperator: this._fb.control("AND", Validators.required),
      conditions: this._fb.array([this.createConditionGroup()]),
    });
    const groupArray = this.subConditionsAndTheirAttributes[
      activeTab
    ]?.formGroup?.get("groupArray") as FormArray;
    groupArray.push(emptyGroup);
    
    // Set up filtering for the new group's condition
    const newGroupIndex = groupArray.length - 1;
    this.setupFilteringForControl(activeTab, newGroupIndex, 0);
  }
  onClickPrevious(activeTab: number) {
    if (activeTab > 0) {
      this.activeTab = activeTab - 1;
    }
  }
  onClickNext(activeTab: number) {
    if (activeTab < this.subConditionsAndTheirAttributes.length) {
      this.activeTab = activeTab + 1;
    }
  }
  checkForFormValidation() {
    let allValid = true;

    // Iterate through the form groups and check their validity
    for (
      let index = 0;
      index < this.subConditionsAndTheirAttributes.length;
      index++
    ) {
      const tab = this.subConditionsAndTheirAttributes[index];
      const formGroup = tab.formGroup as FormGroup;
      const groupArray = formGroup.get("groupArray") as FormArray;

      // Subscribe to the valueChanges of the formGroup to track validity
      formGroup.valueChanges.subscribe(() => {
        this.checkFormValidityAndUpdateMessage(formGroup);
      });

      // Loop through each item in the FormArray (groupArray)
      for (const group of groupArray.controls) {
        const conditionsArray = group.get("conditions") as FormArray;

        // Loop through each condition inside conditionsArray
        for (const conditionGroup of conditionsArray.controls) {
          const attributeControl = conditionGroup.get("attribute");
          const conditionControl = conditionGroup.get("condition");
          const valueControl = conditionGroup.get("value");

          // If any control is invalid, mark it as touched and set the activeTab
          if (
            attributeControl?.invalid ||
            conditionControl?.invalid ||
            valueControl?.invalid
          ) {
            attributeControl?.markAsTouched();
            conditionControl?.markAsTouched();
            valueControl?.markAsTouched();

            this.activeTab = index; // Set the active tab to the first invalid one
            allValid = false;
            this.invalidFormMessage = "Please fill all the values in the form";
            return; // Exit the function as we found the first invalid form
          }
        }
      }
    }
    if (allValid) {
      this.isFormValid = true;
      this._ruleEngineService.setPopUpValue(false); // All forms are valid
    }
  }

  checkFormValidityAndUpdateMessage(formGroup: FormGroup) {
    // This method checks the validity of the entire form group and updates the message accordingly
    if (formGroup.valid) {
      this.invalidFormMessage = ""; // Clear the message when the form becomes valid
    } else {
      this.invalidFormMessage = "Please fill all the values in the form"; // Keep the message if the form is invalid
    }
  }

  onClickSubmit() {
    this.checkForFormValidation();
    if (this.isFormValid) {
      this._ruleEngineService.setFieldWithUpdatedPredicate(
        this.subConditionsAndTheirAttributes
      );
	  this.dialogRef.close();
    }
  }
}
